import classnames from 'classnames'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { getInstrument } from 'finviz-charts/app/utils/chart'
import * as React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  RectangleProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { parseRouteInitData } from '../../../main/util'
import { formatValue } from '../../bubbles/utils'
import { Quote, TIMEFRAME } from '../../charts-lib'
import { getDate } from '../../header/utils'
import { decodeQueryString } from '../../queryString'
import ChartTooltip from './ChartTooltip'
import { InsiderVolumeBar } from './InsiderVolumeBar'
import {
  BAR_BORDER_RADIUS,
  BUY_BAR_COLOR,
  BarType,
  CHART_HEIGHT,
  CURSOR_STYLE,
  LINE_COLOR,
  SALE_BAR_COLOR,
  STROKE_COLOR,
  TOOLTIP_WRAPPER_STYLE,
  TRANSPARENT_CURSOR,
  X_TICK_STYLE,
  Y_TICK_STYLE,
} from './constants'
import { ChartData, TradingVolumeChartInit } from './types'
import { filterDataForChart } from './utils'

interface InsiderTradingVolumeChartProps {
  className?: string
}

interface CustomCursorProps extends Partial<Omit<RectangleProps, 'points'>> {
  numberOfDatapoints: number
  isActive: boolean
  points?: [{ x: number; y: number }]
}

function CustomCursor({ numberOfDatapoints, points, ...props }: CustomCursorProps) {
  const cursorWidth = props.width! / numberOfDatapoints
  const x = points![0].x - cursorWidth / 2
  const y = points![0].y
  return (
    <Rectangle {...props} x={x} y={y} width={cursorWidth} {...(props.isActive ? CURSOR_STYLE : TRANSPARENT_CURSOR)} />
  )
}

function xAxisTickFormatter(value: any) {
  if (!Number.isFinite(value)) return ''

  const date = getDate(value)
  return date.getMonth() === 0 ? date.getFullYear().toString() : format(date, 'MMM')
}

export function InsiderTradingVolumeChart({ className }: InsiderTradingVolumeChartProps) {
  const [isActive, setIsActive] = React.useState(false)
  const [chartData, setChartData] = React.useState<ChartData[]>([])
  const [activeKey, setActiveKey] = React.useState<BarType | null>(null)

  const initData = React.useMemo(() => {
    const data = parseRouteInitData<TradingVolumeChartInit>({ elementId: 'insider-init-data' })

    return {
      data,
      domain: data?.length
        ? [
            getDate(startOfMonth(getDate(data[0].date))).getTime(),
            getDate(endOfMonth(getDate(data[data.length - 1].date))).getTime(),
          ]
        : [],
    }
  }, [])

  const hasInsiderTradingHistory = React.useMemo(
    () => initData.data?.some((x) => x.saleTransactionCount > 0 || x.buyTransactionCount > 0),
    [initData]
  )

  React.useEffect(() => {
    const { t: ticker } = decodeQueryString()
    async function fetchQuoteData() {
      if (!initData.data?.length) return
      const quote = await Quote.get({ ticker, instrument: getInstrument(ticker), timeframe: TIMEFRAME.d })
      const dateFrom = initData.data[0].date
      const dateTo = initData.data[initData.data.length - 1]!.date
      const data = filterDataForChart(quote, dateFrom, dateTo)
      setChartData(data.map((row, index) => ({ ...row, insider: initData.data?.[index] })))
    }

    if (hasInsiderTradingHistory) fetchQuoteData()
  }, [initData, hasInsiderTradingHistory])

  const commonProps = React.useMemo(
    () => ({
      bar: {
        yAxisId: 'yAxisVolume',
        activeBar: false,
        shape: InsiderVolumeBar,
        radius: BAR_BORDER_RADIUS,
        isAnimationActive: false,
        onMouseLeave: () => setActiveKey(null),
      },
      xAxis: {
        dataKey: 'date',
        scale: 'time' as const,
        type: 'number' as const,
        dy: 5,
        tick: X_TICK_STYLE,
        tickLine: false,
        axisLine: false,
        domain: initData.domain,
        tickFormatter: xAxisTickFormatter,
      },
      yAxis: {
        tick: Y_TICK_STYLE,
        tickLine: false,
        axisLine: false,
        tickFormatter: (value: number) => formatValue(value),
      },
    }),
    [initData.domain]
  )

  if (!initData.data?.length || !hasInsiderTradingHistory) return null

  return (
    <div className={classnames('rounded-md border border-primary p-1', className)}>
      <div className="h-10">
        <span className="ml-2 mt-4 h-10 text-sm text-gray-600 font-medium dark:text-gray-200">
          Insider Trading Volume
        </span>
      </div>
      <div
        /**
         * Fix a problem with chart tooltip not hiding correctly on mouse leave
         * See: https://github.com/recharts/recharts/issues/2726
         */
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
      >
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <ComposedChart data={initData.data} barGap={2} barCategoryGap={0}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={STROKE_COLOR} />
            <XAxis {...commonProps.xAxis} xAxisId="xAxisLine" hide />
            <XAxis {...commonProps.xAxis} />
            <YAxis {...commonProps.yAxis} yAxisId="yAxisVolume" />
            <YAxis {...commonProps.yAxis} yAxisId="yAxisClose" orientation="right" domain={['dataMin', 'auto']} />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              position={{ y: 0 }}
              isAnimationActive={false}
              cursor={<CustomCursor isActive={isActive} numberOfDatapoints={initData.data.length} />}
              content={<ChartTooltip isActive={isActive} activeKey={activeKey} />}
            />
            <Line
              data={chartData}
              dataKey="close"
              xAxisId="xAxisLine"
              yAxisId="yAxisClose"
              dot={false}
              activeDot={false}
              stroke={LINE_COLOR}
              isAnimationActive={false}
            />
            <Bar
              {...commonProps.bar}
              dataKey="saleAggregated"
              name={BarType.Sale}
              fill={SALE_BAR_COLOR}
              onMouseEnter={() => setActiveKey(BarType.Sale)}
            />
            <Bar
              {...commonProps.bar}
              dataKey="buyAggregated"
              name={BarType.Buy}
              fill={BUY_BAR_COLOR}
              onMouseEnter={() => setActiveKey(BarType.Buy)}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
